import classnames from 'classnames';
import { useEffect, useState } from 'react';
import { PopoverHostShownContext } from '@sorrem-client/ui/components';
import styles from './SlidePanel.module.scss';

export type SlideDirection = 'left' | 'right' | 'up' | 'down';

const DIRECTION_CLASS: Record<SlideDirection, string> = {
    left: 'slideLeft',
    right: 'slideRight',
    up: 'slideUp',
    down: 'slideDown',
};

/** How long the slide takes (`translate` in SlidePanel.module.scss): a panel counts as settled after it even if no transition ends. */
export const SLIDE_MS = 1000;

interface SlidePanelProps {
    /** Which edge this panel exits toward when hidden. */
    direction: SlideDirection;
    isVisible: boolean;
    className?: string;
    children?: React.ReactNode;
}

/**
 * The shared slide-in/out mechanism every Character Home panel (tab content,
 * character creation's panels) uses instead of being mounted/unmounted on
 * tab change. Every panel for every tab is mounted for Character Home's
 * whole lifetime (see `CharacterHome.tsx`) - switching tabs only ever
 * toggles `visible`, so the persistent 3D viewport behind this layout never
 * has to be interrupted by a remount.
 *
 * Panels that share a spot on screen (e.g. every tab's right-hand panel)
 * get the same placement class from `CharacterHomePanels.module.scss`, so they
 * overlap, and `visible` picks which one is shown/interactive.
 *
 * The hidden state translates the panel by its own size plus its
 * `--slide-offset` (its distance from that screen edge) in the direction it
 * exits, so it ends up just off screen.
 *
 * Popovers inside it (callouts, menus) show only once it has slid in and
 * holds still, and hide as soon as it starts to leave
 * (`PopoverHostShownContext`): nothing points at a panel the player can't see.
 */
export function SlidePanel({ direction, isVisible, className, children }: SlidePanelProps): JSX.Element {
    const [isSettled, setIsSettled] = useState(false);
    useEffect(() => {
        if (!isVisible) {
            setIsSettled(false);
            return undefined;
        }
        // Settled when the slide ends, or after its length if no transition runs.
        const timer = setTimeout(() => setIsSettled(true), SLIDE_MS);
        return () => clearTimeout(timer);
    }, [isVisible]);

    return (
        <div
            className={classnames(
                styles.slidePanel,
                styles[DIRECTION_CLASS[direction]],
                isVisible && styles.visible,
                className,
            )}
            aria-hidden={!isVisible}
            onTransitionEnd={(event): void => {
                if (isVisible && event.target === event.currentTarget && event.propertyName === 'translate') {
                    setIsSettled(true);
                }
            }}
        >
            <PopoverHostShownContext.Provider value={isVisible && isSettled}>
                {children}
            </PopoverHostShownContext.Provider>
        </div>
    );
}
